import { useCallback, useEffect, useState } from 'react';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { avisoBreveDeError } from '../../logica/estados';

/** Ganchos y utilidades de «Listas y RRPP» (10.9 a 10.11) sin dibujo. */

/** Carga con reintento: `datos` de la última respuesta de `cargar`, `error` si falló y `recargar()` para volver a pedir. */
export function useCarga<T>(cargar: (() => Promise<T>) | null, claves: readonly unknown[]): { datos: T | null; error: unknown; recargar: () => void; cargando: boolean } {
    const [estado, setEstado] = useState<{ firma: string; datos: T | null; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const firma = JSON.stringify([...claves, intento]);
    useEffect(() => {
        if (cargar === null) {
            return;
        }
        let vivo = true;
        cargar()
            .then((d) => {
                if (vivo) {
                    setEstado({ firma, datos: d, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setEstado((x) => ({ firma, datos: x?.datos ?? null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
        // `cargar` cambia en cada pintado: lo que manda son las claves.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firma]);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    const actual = estado?.firma === firma;
    return { datos: estado?.datos ?? null, error: actual ? estado.error : null, recargar, cargando: !actual };
}

/** El código estable de un error de la API (`errors[0].code` si lo hay; si no, el del Problem). */
export function codigoDe(error: unknown): string | null {
    if (error instanceof ErrorApi) {
        return error.problema.errors?.[0]?.code ?? error.codigo;
    }
    return null;
}

/** Aviso breve de un error al escribir (solo lectura con el texto de la API; si no, el texto dado o el de la API). */
export function useAvisarError() {
    const avisar = useAvisoBreve();
    return useCallback(
        (error: unknown, texto?: string) => {
            const solo = avisoBreveDeError(error);
            const api = error instanceof ErrorApi ? (error.problema.detail ?? error.problema.title) : null;
            avisar({ tipo: 'error', texto: solo ?? texto ?? api ?? 'No se ha podido guardar. Inténtalo de nuevo.' });
        },
        [avisar],
    );
}

/** `datetime-local` («2026-10-17T23:30») de un instante, en la hora del navegador. */
export function aLocal(iso: string | null | undefined): string {
    if (iso == null || iso === '') {
        return '';
    }
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const d = (n: number) => String(n).padStart(2, '0');
    return `${String(f.getFullYear())}-${d(f.getMonth() + 1)}-${d(f.getDate())}T${d(f.getHours())}:${d(f.getMinutes())}`;
}

/** Un `datetime-local` a instante ISO (null si está vacío). */
export function deLocal(valor: string): string | null {
    if (valor === '') {
        return null;
    }
    const f = new Date(valor);
    return Number.isNaN(f.getTime()) ? null : f.toISOString();
}

export function iniciales(nombre: string): string {
    return nombre
        .split(/\s+/)
        .filter((x) => x !== '')
        .slice(0, 2)
        .map((x) => x[0]?.toLocaleUpperCase('es') ?? '')
        .join('');
}
